// @ts-nocheck
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadQuote from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuote";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import setEditingSortOrder from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetEditingSortOrder";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import quoteAddToCart from "@store/Framework/Store/Store/Pages/Cart/Handlers/QuoteAddToCart";
import AddToCartParameter from "@store/Framework/Store/Store/Pages/Cart/Handlers/QuoteAddToCart";
import ProductSelector, { ProductSelectorStyles } from "@store/Overrides/Components/ProductSelector";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
    quoteId: state.pages.rfqQuoteDetails.quoteId,
});

const mapDispatchToProps = {
    setEditingSortOrder,
    addToCart: makeHandlerChainAwaitable<Parameters<typeof AddToCartParameter>[0], CartLineModel>(quoteAddToCart),
    loadQuote,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqQuoteDetailsOptionsStyles {
    wrapper?: InjectableCss;
    productSelectorStyles?: ProductSelectorStyles;
    addItemsAccordion?: AccordionPresentationProps;
}

export const rfqQuoteDetailsOptionsStyles: RfqQuoteDetailsOptionsStyles = {
    wrapper: {
        css: css`
            ${x => spacingStyles(null, x.theme.design.typography.spacing.large)};
        `,
    },
    addItemsAccordion: {
        css: css`
            ${x => spacingStyles(null, x.theme.design.typography.spacing.larger)};
        `,
    },
    productSelectorStyles: {
        formWrapper: {
            css: css`
                align-items: flex-end;
            `,
        },
        qtyTextField: {
            labelProps: {
                css: css`
                    display: none;
                `,
            },
            css: css`
                display: none;
            `,
        },
    },
};

const styles = rfqQuoteDetailsOptionsStyles;

const RfqQuoteDetailsOptions = ({ quoteState, quoteId, addToCart, loadQuote }: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [addItemsToListIsOpen, setAddItemsToListIsOpen] = useState(false);

    const canEditQuote = quoteState.value && quoteState.value.isSalesperson;
    const hideOptions =
        quoteState.value &&
        (quoteState.value.status === "QuoteProposed" || quoteState.value.status === "AwaitingApproval");

    if (!canEditQuote || !quoteId || hideOptions) {
        return null;
    }

    const addItemsToListClickHandler = () => {
        setAddItemsToListIsOpen(!addItemsToListIsOpen);
    };

    const selectProductHandler = async (productInfo: ProductInfo) => {
        const cartLine = (await addToCart({
            productId: productInfo.productId,
            qtyOrdered: 1,
            unitOfMeasure: "",
            quoteId,
        })) as CartLineModel;

        if (cartLine) {
            loadQuote({ quoteId });
            toasterContext.addToast({ body: translate("Item Added"), messageType: "success" });
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Accordion headingLevel={2} {...styles.addItemsAccordion}>
                <AccordionSection
                    title={translate("Add Items To Quote")}
                    expanded={addItemsToListIsOpen}
                    onTogglePanel={addItemsToListClickHandler}
                >
                    <ProductSelector
                        selectButtonTitle={translate("Add to Quote")}
                        skipInventoryValidation={true}
                        denyFixedConfiguration={true}
                        onSelectProduct={selectProductHandler}
                        productIsConfigurableMessage={siteMessage("ListUpload_CannotOrderConfigurable")}
                        productIsUnavailableMessage={siteMessage("Product_NotFound")}
                        extendedStyles={styles.productSelectorStyles}
                    />
                </AccordionSection>
            </Accordion>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RfqQuoteDetailsOptions),
    definition: {
        group: "RFQ Quote Details",
        allowedContexts: ["RfqQuoteDetailsPage"],
        displayName: "Add Products",
    },
};

export default widgetModule;
